import type { ReactNode } from "react";
import { toLocalIsoDate } from "../../utils/DateUtils";

export type TimeFrameFilter = "ANY" | "FLEXIBLE" | "TODAY" | "NEXT_7_DAYS" | "THIS_WEEKEND" | "CUSTOM";
export type DistanceFilter = "ANY" | "1" | "5" | "10" | "25";
export type BoardTypeFilter = "PAID_GIGS" | "VOLUNTEER_GIGS" | "BUSINESS_LISTINGS";
export type PaySortOption = "DEFAULT" | "PAY_DESC" | "PAY_ASC";

type BoardTypeToggleProps = {
  id: string;
  value: BoardTypeFilter | null;
  onChange: (value: BoardTypeFilter) => void;
  layout?: "compact" | "chooser";
};

const HomeownerGigIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 10v10h14V10" />
    <path d="M9 20v-6h6v6" />
  </svg>
);

const VolunteerGigIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-7-4.35-9.5-8.5A5.3 5.3 0 0 1 12 6a5.3 5.3 0 0 1 9.5 6.5C19 16.65 12 21 12 21Z" />
    <path d="M8 12h8" />
    <path d="M12 8v8" />
  </svg>
);

const BusinessJobIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21h16" />
    <path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16" />
    <path d="M9 7h1" />
    <path d="M14 7h1" />
    <path d="M9 11h1" />
    <path d="M14 11h1" />
    <path d="M9 15h1" />
    <path d="M14 15h1" />
  </svg>
);

export const BoardTypeToggle = ({ id, value, onChange, layout = "compact" }: BoardTypeToggleProps) => {
  const options: Array<{
    value: BoardTypeFilter;
    label: string;
    lineOne: string;
    lineTwo: string;
    Icon: () => ReactNode;
    iconToneClassName: string;
    description: string;
  }> = [
    {
      value: "PAID_GIGS",
      label: "Homeowner Gigs",
      lineOne: "Homeowner",
      lineTwo: "Gigs",
      Icon: HomeownerGigIcon,
      iconToneClassName: "bg-sky-100 text-sky-700",
      description: "Find paid gigs posted by local homeowners.",
    },
    {
      value: "BUSINESS_LISTINGS",
      label: "Business Jobs",
      lineOne: "Business",
      lineTwo: "Jobs",
      Icon: BusinessJobIcon,
      iconToneClassName: "bg-amber-100 text-amber-700",
      description: "Explore jobs posted by local businesses.",
    },
    {
      value: "VOLUNTEER_GIGS",
      label: "Volunteer Opportunities",
      lineOne: "Volunteer",
      lineTwo: "Opportunities",
      Icon: VolunteerGigIcon,
      iconToneClassName: "bg-emerald-100 text-emerald-700",
      description: "Discover volunteer opportunities with local organizations.",
    },
  ];

  if (layout === "chooser") {
    return (
      <section aria-labelledby={`${id}-label`}>
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-myteengig-primary">Opportunities</p>
        <h1 id={`${id}-label`} className="mt-3 text-3xl font-semibold tracking-tight text-myteengig-text-primary sm:text-4xl">
          What kind of opportunity are you looking for?
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-myteengig-text-muted sm:text-base">
          Choose a category to see available opportunities and search within it.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {options.map((option) => {
            const Icon = option.Icon;
            return (
              <button
                key={option.value}
                type="button"
                aria-label={option.label}
                onClick={() => onChange(option.value)}
                className="group flex min-h-52 flex-col items-start rounded-2xl border border-myteengig-border bg-white p-6 text-left shadow-sm transition hover:border-myteengig-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-myteengig-primary/30"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${option.iconToneClassName}`}>
                  <Icon />
                </span>
                <span className="mt-6 text-xl font-semibold text-myteengig-text-primary">{option.label}</span>
                <span className="mt-2 text-sm leading-6 text-myteengig-text-muted">{option.description}</span>
                <span className="mt-auto pt-5 text-sm font-semibold text-myteengig-primary group-hover:underline">Browse opportunities →</span>
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <div className="rounded-2xl border border-myteengig-border bg-white p-3 shadow-sm sm:p-4">
      <p id={`${id}-label`} className="mb-3 text-sm font-semibold text-myteengig-text-primary">
        Choose an opportunity type
      </p>
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        className="grid h-[68px] grid-cols-3 overflow-hidden rounded-lg border border-myteengig-border bg-myteengig-background/60 p-1 sm:h-[58px]"
      >
        {options.map((option) => {
          const isActive = value === option.value;
          const Icon = option.Icon;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isActive}
              aria-label={option.label}
              onClick={() => onChange(option.value)}
              className={`flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 text-center text-[0.68rem] font-semibold leading-tight transition focus:outline-none focus:ring-2 focus:ring-myteengig-primary/30 sm:flex-row sm:gap-1.5 sm:px-1.5 sm:text-xs ${
                isActive
                  ? "bg-myteengig-primary text-white shadow-sm"
                  : "text-myteengig-text-muted hover:bg-myteengig-background hover:text-myteengig-text-primary"
              }`}
            >
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${isActive ? "bg-white text-myteengig-primary" : option.iconToneClassName}`}>
                <Icon />
              </span>
              <span className="min-w-0">
                <span className="block">{option.lineOne}</span>
                <span className="block">{option.lineTwo}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

type TableFiltersProps = {
  search: string;
  paySort: PaySortOption;
  timeFrame: TimeFrameFilter;
  distance: DistanceFilter;
  customFromDate: string;
  customToDate: string;
  actionSlot?: ReactNode;
  onSearchChange: (value: string) => void;
  onPaySortChange: (value: PaySortOption) => void;
  onTimeFrameChange: (value: TimeFrameFilter) => void;
  onDistanceChange: (value: DistanceFilter) => void;
  onCustomFromDateChange: (value: string) => void;
  onCustomToDateChange: (value: string) => void;
};

const TableFilters = ({
  search,
  paySort,
  timeFrame,
  distance,
  customFromDate,
  customToDate,
  actionSlot,
  onSearchChange,
  onPaySortChange,
  onTimeFrameChange,
  onDistanceChange,
  onCustomFromDateChange,
  onCustomToDateChange,
}: TableFiltersProps) => {
  const minDate = toLocalIsoDate(new Date());
  const minToDate = customFromDate && customFromDate > minDate ? customFromDate : minDate;

  return (
    <div className="mb-5 rounded-2xl border border-myteengig-border bg-gradient-to-r from-white via-myteengig-background to-white p-4 shadow-sm sm:p-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-12 lg:items-end">
        <div className="col-span-2 min-w-0 sm:col-span-2 lg:col-span-12">
          <label htmlFor="gig-filter-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Search
          </label>
          <input
            id="gig-filter-search"
            type="text"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Title, category, city, state, zip, remote"
            className="w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-base text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          />
        </div>

        <div className="min-w-0 lg:col-span-4">
          <label htmlFor="gig-filter-sort" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Pay
          </label>
          <select
            id="gig-filter-sort"
            value={paySort}
            onChange={(event) => onPaySortChange(event.target.value as PaySortOption)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="DEFAULT">Any Pay</option>
            <option value="PAY_DESC">Highest paying</option>
            <option value="PAY_ASC">Lowest paying</option>
          </select>
        </div>

        <div className="min-w-0 lg:col-span-4">
          <label htmlFor="gig-filter-time-frame" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Time Frame
          </label>
          <select
            id="gig-filter-time-frame"
            value={timeFrame}
            onChange={(event) => onTimeFrameChange(event.target.value as TimeFrameFilter)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="ANY">Any time</option>
            <option value="FLEXIBLE">Flexible</option>
            <option value="TODAY">Today</option>
            <option value="NEXT_7_DAYS">Next 7 days</option>
            <option value="THIS_WEEKEND">This weekend</option>
            <option value="CUSTOM">Custom range</option>
          </select>
        </div>

        <div className="min-w-0 lg:col-span-4">
          <label htmlFor="gig-filter-distance" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
            Distance
          </label>
          <select
            id="gig-filter-distance"
            value={distance}
            onChange={(event) => onDistanceChange(event.target.value as DistanceFilter)}
            className="h-[50px] w-full rounded-lg border border-myteengig-border bg-white px-4 text-sm font-medium text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
          >
            <option value="ANY">Any distance</option>
            <option value="1">Within 1 mile</option>
            <option value="5">Within 5 miles</option>
            <option value="10">Within 10 miles</option>
            <option value="25">Within 25 miles</option>
          </select>
        </div>

        {actionSlot ? <div className="min-w-0 sm:col-span-2 lg:col-span-12 lg:justify-self-end">{actionSlot}</div> : null}
      </div>

      {timeFrame === "CUSTOM" ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="gig-filter-custom-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              From
            </label>
            <input
              id="gig-filter-custom-from"
              type="date"
              min={minDate}
              value={customFromDate}
              onChange={(event) => onCustomFromDateChange(event.target.value)}
              className="h-[44px] w-full rounded-lg border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            />
          </div>
          <div>
            <label htmlFor="gig-filter-custom-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              To
            </label>
            <input
              id="gig-filter-custom-to"
              type="date"
              min={minToDate}
              value={customToDate}
              onChange={(event) => onCustomToDateChange(event.target.value)}
              className="h-[44px] w-full rounded-lg border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TableFilters;
